/**
 * Shared avatar helpers.
 *
 * The user record can carry the avatar in three shapes (in priority order):
 *
 *   1. `user.avatar` = `"data:image/...;base64,..."` or a plain URL
 *      → uploaded photo or animal-emoji-based avatar
 *   2. `user.avatar` = `"emoji:🙂"`
 *      → the picker's emoji shortcut
 *   3. `user.picture` = `"https://..."`
 *      → legacy Google-OAuth profile picture, kept for older accounts
 *
 * `resolveAvatar` normalises those into a single small descriptor so every
 * place that renders a user's avatar shares one code path.
 *
 * `<UserAvatar />` is the reusable component. Prefer it over hand-rolling
 * a rounded div — it keeps sizing / border / fallback icon consistent.
 */
import React from 'react';
import { User as UserIcon } from 'lucide-react';

/**
 * @typedef {Object} ResolvedAvatar
 * @property {'image' | 'emoji' | 'none'} kind
 * @property {string | null} value  // src URL, single emoji, or null
 */

/**
 * Normalise a user record into a renderable avatar descriptor.
 * @param {{avatar?: string, picture?: string} | null | undefined} user
 * @returns {ResolvedAvatar}
 */
export function resolveAvatar(user) {
  const av = user?.avatar || '';
  if (av.startsWith('data:image') || av.startsWith('http')) {
    return { kind: 'image', value: av };
  }
  if (av.startsWith('emoji:')) {
    return { kind: 'emoji', value: av.slice(6) };
  }
  if (user?.picture) return { kind: 'image', value: user.picture };
  return { kind: 'none', value: null };
}

// Tailwind class maps — kept as static strings so tailwind's purge picks them up.
const SIZE = {
  xs: 'w-6 h-6',
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-16 h-16',
  xl: 'w-24 h-24',
};
const EMOJI_SIZE = {
  xs: 'text-sm',
  sm: 'text-base',
  md: 'text-xl',
  lg: 'text-3xl',
  xl: 'text-5xl',
};
const ICON_SIZE = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12',
};

/**
 * Reusable avatar circle.
 *
 * @param {Object} props
 * @param {{avatar?: string, picture?: string, name?: string} | null} props.user
 * @param {'xs'|'sm'|'md'|'lg'|'xl'} [props.size='md']
 * @param {string} [props.className]  extra classes appended to the wrapper
 * @param {string} [props.altName]  alt-text override; defaults to user.name
 * @param {string} [props.testId]
 */
export function UserAvatar({ user, size = 'md', className = '', altName, testId }) {
  const a = resolveAvatar(user);
  const wrapper =
    `${SIZE[size]} rounded-full border border-zinc-200 bg-zinc-100 ` +
    `flex items-center justify-center overflow-hidden flex-shrink-0 ${className}`.trim();

  if (a.kind === 'image') {
    return (
      <div className={wrapper} data-testid={testId}>
        <img
          src={a.value}
          alt={altName || user?.name || 'Avatar'}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  if (a.kind === 'emoji') {
    return (
      <div className={wrapper} data-testid={testId}>
        <span className={`${EMOJI_SIZE[size]} leading-none`}>{a.value}</span>
      </div>
    );
  }
  return (
    <div className={wrapper} data-testid={testId}>
      <UserIcon className={`${ICON_SIZE[size]} text-zinc-500`} />
    </div>
  );
}

export default UserAvatar;
